import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { FlightItineraryEnum } from '@constants/flight.constant';
import { AirportDDL } from '@models/airport.model';
import { AccountOrderResolver } from '@models/resolver.model';
import { FareRuleMapKeyData, FareRuleMapKeyReq } from '@models/master-data.model';
import { Flight } from '@models/order.model';
import { DropdownListService } from '@services/dropdown-list.service';
import { MasterDataService } from '@services/master-data.service';
import { OrderService } from '@services/order.service';
import { ToastService } from '@services/toast.service';
import { allBookingIsAmadeus } from '@utils/order.util';
import { EMPTY, forkJoin, from, map, of, switchMap } from 'rxjs';

export const accountOrderResolver: ResolveFn<AccountOrderResolver> = (route) => {
    const orderService = inject(OrderService);
    const dropdownService = inject(DropdownListService);
    const masterDataService = inject(MasterDataService);
    const toast = inject(ToastService);
    const router = inject(Router);
    const { orderId } = route.params;

    return orderService.getOrderDetailById(orderId).pipe(
        switchMap((res) => {
            const detail = res?.Data;
            let group = of({} as Record<string, AirportDDL>);
            if (detail) {
                group = dropdownService.getAirportMap();
            }

            return forkJoin([of(detail), group]);
        }),
        switchMap(([detail, group]) => {
            if (!detail) {
                toast.error(
                    $localize`:@@search-order.toast.title: Thông báo`,
                    $localize`:@@search-order.toast.error: Không tìm thấy thông tin chặng bay!`
                );
                return from(router.navigate(['/account/orders'])).pipe(switchMap(() => EMPTY));
            }

            if (allBookingIsAmadeus(detail)) {
                return of({ order: detail, fareRuleMapKeys: {}, airportGroup: group });
            }

            const orderBookings = detail.OrderBookings ?? [];
            const ItineraryId = detail.ItineraryId;
            const flights: (Flight & { SystemCode: string })[] = orderBookings.flatMap((o) =>
                o.Flights.map((f) => ({ ...f, SystemCode: o.SystemCode }))
            );

            if (ItineraryId === FlightItineraryEnum.ROUND_TRIP && flights.length === 1) {
                flights.push({
                    ...flights[0],
                    Origin: flights[0].Destination,
                    Destination: flights[0].Origin,
                });
            }
            const req: FareRuleMapKeyReq[] = flights.map((f) => {
                const origin = group[f.Origin];
                const destination = group[f.Destination];
                return {
                    Origin: f.Origin,
                    Destination: f.Destination,
                    CountryCodeOrigin: origin.RegionCode,
                    CountryCodeDestination: destination.RegionCode,
                    IsInternational: origin.International || destination.International,
                    CarrierCode: f.CarrierCode,
                    SystemCode: f.SystemCode,
                    FareCode: f.FareType,
                };
            });

            return masterDataService.getListFareRuleMapKey(req).pipe(
                map((res) => {
                    const fareRule = res.reduce(
                        (acc, cur) => {
                            acc[cur.Origin.concat('-', cur.Destination)] = cur.Data;
                            return acc;
                        },
                        {} as Record<string, FareRuleMapKeyData[]>
                    );

                    return { order: detail, fareRuleMapKeys: fareRule, airportGroup: group };
                })
            );
        })
    );
};
